"use client"

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./design-system"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./design-system"
import { Button } from "./design-system"
import { Badge } from "./design-system"
import { Download, FileText, Printer, Share2, Check } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { shareContent } from "../utils/shareUtils"
import { useSportContext } from "../hooks/useSportContext"

export function Reports() {
  const [shareSuccess, setShareSuccess] = useState(false);

  // Sport context - reactive to Settings changes
  const {
    sportCode,
    sportName,
    teamName,
    isBaseball,
    isBasketball,
  } = useSportContext();

  // Log when sport changes
  useEffect(() => {
    if (sportCode) {
      console.log('[Reports] Sport context:', sportCode, teamName);
    }
  }, [sportCode, teamName]);

  const handleShare = async () => {
    const result = await shareContent({
      title: 'Development Reports - SLAM Sports AI',
      text: 'View detailed player development reports and analytics',
    });

    if (result.success) {
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 text-[#F5F5F5]">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Development Reports</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="bg-[#0A0A0A] border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)] text-[#F5F5F5]"
            onClick={handlePrint}
          >
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="bg-[#0A0A0A] border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)] text-[#F5F5F5]">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={`bg-[#0A0A0A] border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)] text-[#F5F5F5] ${shareSuccess ? 'bg-green-600 border-green-600 text-white' : ''}`}
            onClick={handleShare}
          >
            {shareSuccess ? <Check className="mr-2 h-4 w-4" /> : <Share2 className="mr-2 h-4 w-4" />}
            {shareSuccess ? 'Copied!' : 'Share'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="bg-[#0A0A0A] w-full justify-start">
          <TabsTrigger value="summary" className="data-[state=active]:bg-[#E8B800] data-[state=active]:text-white">
            Summary
          </TabsTrigger>
          <TabsTrigger value="velocity" className="data-[state=active]:bg-[#E8B800] data-[state=active]:text-white">
            Velocity
          </TabsTrigger>
          <TabsTrigger value="biomechanics" className="data-[state=active]:bg-[#E8B800] data-[state=active]:text-white">
            Biomechanics
          </TabsTrigger>
          <TabsTrigger value="physical" className="data-[state=active]:bg-[#E8B800] data-[state=active]:text-white">
            Physical
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-6">
          <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
            <CardHeader>
              <CardTitle>Development Summary</CardTitle>
              <CardDescription className="text-[#999999]">Overall progress and key metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-[#111111] p-4 rounded-[14px]">
                    <div className="text-sm text-[#999999] mb-1">Current Velocity</div>
                    <div className="text-3xl font-bold">89 mph</div>
                    <div className="text-sm text-blue-500 mt-1">+11 mph from baseline</div>
                  </div>
                  <div className="bg-[#111111] p-4 rounded-[14px]">
                    <div className="text-sm text-[#999999] mb-1">Biomechanics Score</div>
                    <div className="text-3xl font-bold">86%</div>
                    <div className="text-sm text-blue-500 mt-1">+18% from baseline</div>
                  </div>
                  <div className="bg-[#111111] p-4 rounded-[14px]">
                    <div className="text-sm text-[#999999] mb-1">Physical Development</div>
                    <div className="text-3xl font-bold">92%</div>
                    <div className="text-sm text-blue-500 mt-1">+27% from baseline</div>
                  </div>
                </div>

                <div className="bg-[#111111] p-4 rounded-[14px]">
                  <h3 className="text-lg font-medium mb-4">Development Timeline</h3>
                  <div className="relative">
                    <div className="absolute top-0 bottom-0 left-7 border-l border-[#1A1A1A]" />
                    <ul className="space-y-6 relative">
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#E8B800]" />
                        <div className="font-medium">Initial Assessment (Jan 2023)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Baseline measurements: 78 mph velocity, 68% biomechanics efficiency, 165 lbs
                        </p>
                      </li>
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#E8B800]" />
                        <div className="font-medium">3-Month Progress (Apr 2023)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Early improvements: 81 mph velocity, 74% biomechanics efficiency, 171 lbs
                        </p>
                      </li>
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#E8B800]" />
                        <div className="font-medium">6-Month Progress (Jul 2023)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Significant gains: 84 mph velocity, 80% biomechanics efficiency, 177 lbs
                        </p>
                      </li>
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#E8B800]" />
                        <div className="font-medium">Current Status (Dec 2023)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Latest measurements: 89 mph velocity, 86% biomechanics efficiency, 187 lbs
                        </p>
                      </li>
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#555555]" />
                        <div className="font-medium">Projected (14 Months)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Expected progress: 92 mph velocity, 90% biomechanics efficiency, 192 lbs
                        </p>
                      </li>
                      <li className="ml-10 relative">
                        <div className="absolute -left-10 mt-1.5 h-3 w-3 rounded-full bg-[#555555]" />
                        <div className="font-medium">Projected (3 Years)</div>
                        <p className="text-sm text-[#999999] mt-1">
                          Long-term potential: 94 mph velocity, 95% biomechanics efficiency, 195 lbs
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="velocity" className="mt-6">
          <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
            <CardHeader>
              <CardTitle>Velocity Development Report</CardTitle>
              <CardDescription className="text-[#999999]">Detailed velocity metrics and progression</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1A1A1A]">
                    <TableHead className="text-[#999999]">Date</TableHead>
                    <TableHead className="text-[#999999]">Max Velocity</TableHead>
                    <TableHead className="text-[#999999]">Avg Velocity</TableHead>
                    <TableHead className="text-[#999999]">Consistency</TableHead>
                    <TableHead className="text-[#999999]">Command</TableHead>
                    <TableHead className="text-[#999999]">Report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell>Jan 2023</TableCell>
                    <TableCell>78 mph</TableCell>
                    <TableCell>76 mph</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        72%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        68%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#999999] hover:text-[#E8B800]">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell>Apr 2023</TableCell>
                    <TableCell>81 mph</TableCell>
                    <TableCell>79 mph</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        75%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        72%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#999999] hover:text-[#E8B800]">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell>Jul 2023</TableCell>
                    <TableCell>84 mph</TableCell>
                    <TableCell>82 mph</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        79%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        76%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#999999] hover:text-[#E8B800]">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell>Oct 2023</TableCell>
                    <TableCell>87 mph</TableCell>
                    <TableCell>85 mph</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        83%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        80%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#999999] hover:text-[#E8B800]">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell>Dec 2023</TableCell>
                    <TableCell>89 mph</TableCell>
                    <TableCell>87 mph</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        86%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">
                        84%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#999999] hover:text-[#E8B800]">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="biomechanics" className="mt-6">
          <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
            <CardHeader>
              <CardTitle>Biomechanics Analysis Report</CardTitle>
              <CardDescription className="text-[#999999]">Detailed biomechanics metrics and progression</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1A1A1A]">
                    <TableHead className="text-[#999999]">Metric</TableHead>
                    <TableHead className="text-[#999999]">Initial</TableHead>
                    <TableHead className="text-[#999999]">Current</TableHead>
                    <TableHead className="text-[#999999]">Change</TableHead>
                    <TableHead className="text-[#999999]">Target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Shoulder Rotation</TableCell>
                    <TableCell>68%</TableCell>
                    <TableCell>85%</TableCell>
                    <TableCell className="text-blue-500">+17%</TableCell>
                    <TableCell>95%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Hip-Shoulder Separation</TableCell>
                    <TableCell>62%</TableCell>
                    <TableCell>78%</TableCell>
                    <TableCell className="text-blue-500">+16%</TableCell>
                    <TableCell>90%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Lead Leg Block</TableCell>
                    <TableCell>70%</TableCell>
                    <TableCell>82%</TableCell>
                    <TableCell className="text-blue-500">+12%</TableCell>
                    <TableCell>92%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Trunk Tilt</TableCell>
                    <TableCell>75%</TableCell>
                    <TableCell>90%</TableCell>
                    <TableCell className="text-blue-500">+15%</TableCell>
                    <TableCell>95%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Arm Path</TableCell>
                    <TableCell>65%</TableCell>
                    <TableCell>84%</TableCell>
                    <TableCell className="text-blue-500">+19%</TableCell>
                    <TableCell>92%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Follow Through</TableCell>
                    <TableCell>72%</TableCell>
                    <TableCell>88%</TableCell>
                    <TableCell className="text-blue-500">+16%</TableCell>
                    <TableCell>94%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="physical" className="mt-6">
          <Card className="bg-[#0A0A0A] border-[#1A1A1A]">
            <CardHeader>
              <CardTitle>Physical Development Report</CardTitle>
              <CardDescription className="text-[#999999]">Detailed physical metrics and progression</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1A1A1A]">
                    <TableHead className="text-[#999999]">Metric</TableHead>
                    <TableHead className="text-[#999999]">Initial</TableHead>
                    <TableHead className="text-[#999999]">Current</TableHead>
                    <TableHead className="text-[#999999]">Change</TableHead>
                    <TableHead className="text-[#999999]">Target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Weight</TableCell>
                    <TableCell>165 lbs</TableCell>
                    <TableCell>187 lbs</TableCell>
                    <TableCell className="text-blue-500">+22 lbs</TableCell>
                    <TableCell>195 lbs</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Muscle Mass</TableCell>
                    <TableCell>120 lbs</TableCell>
                    <TableCell>152 lbs</TableCell>
                    <TableCell className="text-blue-500">+32 lbs</TableCell>
                    <TableCell>160 lbs</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Body Fat %</TableCell>
                    <TableCell>14%</TableCell>
                    <TableCell>11%</TableCell>
                    <TableCell className="text-blue-500">-3%</TableCell>
                    <TableCell>10%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Power Output</TableCell>
                    <TableCell>65%</TableCell>
                    <TableCell>85%</TableCell>
                    <TableCell className="text-blue-500">+20%</TableCell>
                    <TableCell>95%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Core Strength</TableCell>
                    <TableCell>70%</TableCell>
                    <TableCell>88%</TableCell>
                    <TableCell className="text-blue-500">+18%</TableCell>
                    <TableCell>95%</TableCell>
                  </TableRow>
                  <TableRow className="border-[#1A1A1A]">
                    <TableCell className="font-medium">Flexibility</TableCell>
                    <TableCell>75%</TableCell>
                    <TableCell>86%</TableCell>
                    <TableCell className="text-blue-500">+11%</TableCell>
                    <TableCell>90%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
